import { useEffect, useState } from 'react'
import { Car, Fuel } from 'lucide-react'

import { Field } from '../../components/Field'
import { alleReisedokumente } from '../../data/db'
import { useRoadbook } from '../../data/roadbookContext'
import { num, zeile } from '../../lib/format'
import {
  fahrzeug,
  fuhrpark,
  FUEL_LABEL,
  FUEL_UNIT,
  reichweiteKm,
  verbrauchUrteil,
  type FuhrparkEintrag,
} from '../../lib/vehicle'
import { FUEL_KINDS, type FuelKind, type Vehicle } from '../../lib/types'

/**
 * Der Block „Fahrzeug“ im Reiter Daten.
 *
 * Was hier steht, gehört der Reise und nicht dem Gerät: das Buch wird gedruckt
 * und geteilt, und dort muss stehen, womit tatsächlich gefahren wurde. Vier der
 * fünf Angaben waren bis hierher fest im Programm - Kraftstoffart, Zuschlag,
 * Schild und ein empfohlener Verbrauch von 14 Litern -, und alle vier
 * beschrieben denselben VW T3.
 */
export function VehicleEditor() {
  const { trip, tripId, darfSchreiben, setVehicle } = useRoadbook()
  const auto = fahrzeug(trip)
  const einheit = FUEL_UNIT[auto.fuel]
  const reichweite = reichweiteKm(auto)

  if (!darfSchreiben) {
    return (
      <div className="block">
        <h4>
          <Car size={14} /> Fahrzeug
        </h4>
        <p>{fahrzeugZeile(auto) || 'Zu diesem Fahrzeug ist noch nichts eingetragen.'}</p>
      </div>
    )
  }

  return (
    <div className="block">
      <h4>
        <Car size={14} /> Fahrzeug
      </h4>

      <div className="raster-2">
        <Field label="Name">
          <input value={auto.name} onChange={(e) => setVehicle({ name: e.target.value })} />
        </Field>
        <Field label="Kraftstoff">
          <select
            value={auto.fuel}
            onChange={(e) => setVehicle({ fuel: e.target.value as FuelKind })}
          >
            {FUEL_KINDS.map((art) => (
              <option key={art} value={art}>
                {FUEL_LABEL[art]}
              </option>
            ))}
          </select>
        </Field>
        <Field label={`Verbrauch ${einheit}/100 km`}>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            step={0.5}
            value={auto.consumption}
            onChange={(e) => setVehicle({ consumption: Number(e.target.value) })}
          />
        </Field>
        <Field label={`Tankinhalt ${einheit}`}>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            step={1}
            value={auto.tank}
            onChange={(e) => setVehicle({ tank: Number(e.target.value) })}
          />
        </Field>
        {/*
          Prozent im Feld, Anteil im Dokument. Wer 25 tippt, meint ein Viertel
          und nicht das Fünfundzwanzigfache.
        */}
        <Field label="Zeitzuschlag %">
          <input
            type="number"
            inputMode="numeric"
            min={0}
            max={100}
            step={5}
            value={Math.round(auto.surcharge * 100)}
            onChange={(e) => setVehicle({ surcharge: zuschlagAusProzent(e.target.value) })}
          />
        </Field>
        <Field label="Nationalitätszeichen">
          <input
            value={auto.sign}
            maxLength={3}
            placeholder="CH"
            onChange={(e) => setVehicle({ sign: zeichen(e.target.value) })}
          />
        </Field>
      </div>

      <p className="abschluss">
        {reichweite > 0
          ? `Reichweite rund ${num(reichweite)} km je Tankfüllung. `
          : `Mit Tankinhalt und Verbrauch steht hier die Reichweite. `}
        Der Zeitzuschlag gilt für die Fahrzeiten des Routendienstes: der rechnet mit einem
        gewöhnlichen Personenwagen, und wer langsamer unterwegs ist, schlägt hier auf. Übernommen
        wird er nie von selbst — die Etappe zeigt beide Zeiten und der Knopf gehört dir.
      </p>

      <Verbrauchshinweis auto={auto} />
      <Fuhrpark tripId={tripId} onWaehlen={setVehicle} />
    </div>
  )
}

/** Das Fahrzeug als Satz, für die Ansicht ohne Schreibrecht. */
function fahrzeugZeile(auto: Vehicle): string {
  const einheit = FUEL_UNIT[auto.fuel]
  const reichweite = reichweiteKm(auto)
  return zeile([
    auto.name,
    FUEL_LABEL[auto.fuel],
    auto.consumption > 0 && `${String(auto.consumption)} ${einheit}/100 km`,
    auto.tank > 0 && `${String(auto.tank)} ${einheit} Tank`,
    reichweite > 0 && `${num(reichweite)} km Reichweite`,
    auto.surcharge > 0 && `${String(Math.round(auto.surcharge * 100))} % Zeitzuschlag`,
    auto.sign && `Schild ${auto.sign}`,
  ])
}

/**
 * Der Hinweis zu einem Verbrauch, der nicht zur Kraftstoffart passt.
 *
 * Hier stand die Empfehlung, es mögen doch 14 Liter sein - die des Wasserboxers,
 * für jede Reise. Empfohlen wird jetzt nichts mehr: welchen Verbrauch ein
 * fremdes Fahrzeug hat, weiss diese App nicht, und eine Zahl anzubieten, die
 * die Kraftstoffschätzung verdreifacht, ist schlimmer als keine. Übrig bleibt
 * die Frage, ob die Zahl überhaupt die eines Autos sein kann - siehe
 * verbrauchUrteil in lib/vehicle.ts.
 */
function Verbrauchshinweis({ auto }: { auto: Vehicle }) {
  const urteil = verbrauchUrteil(auto)
  if (urteil === 'passt') return null

  const einheit = FUEL_UNIT[auto.fuel]
  return (
    <p className="hinweis">
      <Fuel size={13} />
      <span>
        {auto.consumption} {einheit}/100 km sind für {FUEL_LABEL[auto.fuel]} ungewöhnlich{' '}
        {urteil === 'niedrig' ? 'wenig' : 'viel'}. Sieh die Zahl noch einmal an — die
        Kraftstoffschätzung im Budget hängt daran.
      </span>
    </p>
  )
}

/**
 * Die Fahrzeuge der anderen Reisen dieses Geräts, als Knopfreihe.
 *
 * Sie entstehen aus dem, was ohnehin lokal liegt (siehe fuhrpark in
 * lib/vehicle.ts) - kein zweiter Speicher, keine Liste, die veralten könnte.
 * Gelesen wird beim Öffnen des Reiters, einmal: der Bestand einer fremden Reise
 * ändert sich nicht, während man in dieser hier tippt.
 */
function Fuhrpark({
  tripId,
  onWaehlen,
}: {
  tripId: string
  onWaehlen: (fahrzeug: Vehicle) => void
}) {
  const [liste, setListe] = useState<FuhrparkEintrag[]>([])

  useEffect(() => {
    let abgebrochen = false
    void (async () => {
      try {
        const bestaende = await alleReisedokumente()
        if (!abgebrochen) setListe(fuhrpark(bestaende, tripId))
      } catch (err) {
        // Ohne Fuhrpark lässt sich alles von Hand eintragen. Ein Fehler beim
        // Lesen fremder Bestände darf diesen Reiter nicht aufhalten.
        console.warn('Fuhrpark liess sich nicht lesen', err)
      }
    })()
    return () => {
      abgebrochen = true
    }
  }, [tripId])

  if (liste.length === 0) return null

  return (
    <>
      <p className="abschluss">
        Übernehmen aus einer anderen Reise auf diesem Gerät — ein Druck setzt alle Felder darüber,
        die andere Reise bleibt, wie sie ist.
      </p>
      <div className="knopfreihe">
        {liste.map((eintrag) => (
          <button
            key={`${eintrag.reise}:${eintrag.fahrzeug.name}`}
            type="button"
            className="knopf still"
            onClick={() => onWaehlen(eintrag.fahrzeug)}
          >
            {eintrag.fahrzeug.name}
            {eintrag.reise && ` (${eintrag.reise})`}
          </button>
        ))}
      </div>
    </>
  )
}

/** Prozent aus dem Feld werden zum Anteil im Dokument, und zwar zu einem gültigen. */
function zuschlagAusProzent(roh: string): number {
  const prozent = Number(roh)
  if (!Number.isFinite(prozent)) return 0
  return Math.min(1, Math.max(0, Math.round(prozent) / 100))
}

/**
 * Ein Nationalitätszeichen: Buchstaben, gross, höchstens drei.
 *
 * Auf dem ovalen Schild stehen ein bis drei Buchstaben (CH, D, SLO), und mehr
 * passt dort weder auf dem Papier noch im Kopf der App - die Fläche ist
 * bemessen.
 */
function zeichen(roh: string): string {
  return roh
    .replace(/[^\p{L}]/gu, '')
    .toUpperCase()
    .slice(0, 3)
}
